"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

// Internal context so AnimatedDialogContent knows when the dialog is open
const DialogOpenContext = React.createContext(false)

// Wrap Dialog.Root to pass open state via context
const DialogWithContext = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>
>(({ children, open, onOpenChange, defaultOpen, ...props }, _ref) => {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false)
  const isControlled = open !== undefined
  const isOpen = isControlled ? open : internalOpen

  const handleOpenChange = (next: boolean) => {
    if (!isControlled) setInternalOpen(next)
    onOpenChange?.(next)
  }

  return (
    <DialogOpenContext.Provider value={isOpen}>
      <DialogPrimitive.Root open={isOpen} onOpenChange={handleOpenChange} {...props}>
        {children}
      </DialogPrimitive.Root>
    </DialogOpenContext.Provider>
  )
})
DialogWithContext.displayName = "Dialog"

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 z-50 bg-black/50", className)}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const MOBILE_FULLSCREEN_MEDIA = '(max-width: 639px)'

// Apply critical mobile full-screen overrides that must beat inline styles
function useMobileFullScreenStyle(
  mobileFullScreen: boolean,
  userStyle: React.CSSProperties | undefined
): React.CSSProperties | undefined {
  const [isMobile, setIsMobile] = React.useState(false)

  React.useEffect(() => {
    if (!mobileFullScreen) return
    const mql = window.matchMedia(MOBILE_FULLSCREEN_MEDIA)
    setIsMobile(mql.matches)
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [mobileFullScreen])

  if (!mobileFullScreen || !isMobile) return userStyle

  return {
    ...userStyle,
    // Override any inline maxHeight/height that would prevent full-screen
    maxHeight: '100dvh',
    height: '100dvh',
    maxWidth: 'none',
    boxShadow: 'none',
  }
}

// Fullscreen-on-mobile + centered-modal-on-desktop flex-column layout.
// Used for complex dialogs that manage their own header/content/footer padding.
const PANEL_LAYOUT_CLASSES =
  "left-0 top-0 flex h-dvh w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 p-0 " +
  "min-[640px]:left-[50%] min-[640px]:top-[50%] min-[640px]:h-auto min-[640px]:w-[calc(100vw-1rem)] min-[640px]:max-h-[90vh] min-[640px]:max-w-3xl " +
  "min-[640px]:-translate-x-1/2 min-[640px]:-translate-y-1/2 min-[640px]:rounded-lg " +
  // `border-[3px]` sets a width but no colour, so the border fell back to
  // `currentColor` — i.e. the foreground text colour, rendering near-black on
  // every panel dialog. The default layout never showed this because it names
  // `border-border` explicitly. NewResearchDialog had already worked around it
  // locally with `min-[640px]:border-0`.
  "min-[640px]:border-[3px] min-[640px]:border-border"

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideClose?: boolean
    mobileFullScreen?: boolean
    /** "panel" — fullscreen on mobile, centered flex-col modal on desktop (no built-in padding). */
    layout?: "default" | "panel"
  }
>(({ className, children, hideClose, mobileFullScreen = true, layout = "default", style: userStyle, ...props }, ref) => {
  const isOpen = React.useContext(DialogOpenContext)
  const resolvedStyle = useMobileFullScreenStyle(layout === "default" ? mobileFullScreen : false, userStyle)

  return (
    <DialogPortal>
      <AnimatePresence>
        {isOpen && (
          <>
            <DialogPrimitive.Overlay asChild>
              <motion.div
                key="overlay"
                className="fixed inset-0 z-50 bg-black/50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content
              ref={ref}
              className={cn(
                "fixed z-50 bg-background shadow-[var(--shadow-warm-xl)] overflow-hidden dialog-content-animate",
                layout === "panel"
                  ? PANEL_LAYOUT_CLASSES
                  : cn(
                      "border border-border border-t border-t-primary",
                      mobileFullScreen
                        ? "max-sm:inset-0 max-sm:m-0 max-sm:flex max-sm:flex-col max-sm:h-dvh max-sm:max-h-dvh max-sm:w-screen max-sm:max-w-none max-sm:border-0 max-sm:shadow-none max-sm:overflow-y-auto sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] grid w-full max-w-lg gap-4 p-6"
                        : "left-[50%] top-[50%] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 p-6",
                    ),
                className
              )}
              style={resolvedStyle}
              {...props}
            >
              {/* Top accent is now via border-t border-t-primary on the content shell */}
              {children}
              {!hideClose && (
                <DialogPrimitive.Close
                  className={cn(
                    "absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-sm border border-transparent bg-transparent text-muted-foreground transition-all duration-[140ms]",
                    "hover:bg-muted/40 hover:text-foreground",
                    "focus-visible:outline-none focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-primary/10 focus-visible:ring-offset-0",
                    "disabled:pointer-events-none disabled:opacity-50"
                  )}
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
              )}
            </DialogPrimitive.Content>
          </>
        )}
      </AnimatePresence>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  DialogWithContext as Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
